import { useState } from 'react';
import {
  configApiRef,
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { autheliaLogoutUrl } from './userBlock';

/**
 * Signs out of Backstage and then of Authelia: the sidebar's button, and the
 * « Se reconnecter » of the Odoo screens whose token predates the user's
 * rights (projectEnvironments/tokenGroups.ts).
 *
 * Backstage's sign-out alone is not enough: Authelia keeps its own SSO
 * session, so the next "Compte Waadoo" click would sign the user straight back
 * in without a password. When `app.authelia.baseUrl` is configured, the browser
 * is sent to Authelia's logout page afterwards, which returns to Backstage.
 * Without it (local development, guest sign-in), only the Backstage session
 * ends.
 */
export function useSignOut(): {
  signOut: () => Promise<void>;
  pending: boolean;
} {
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const configApi = useApi(configApiRef);
  const [pending, setPending] = useState(false);

  const autheliaBaseUrl = configApi.getOptionalString('app.authelia.baseUrl');
  const appBaseUrl = configApi.getString('app.baseUrl');

  const signOut = async () => {
    setPending(true);
    try {
      // Ends the Backstage session — for an Authelia sign-in, the auth
      // backend revokes the refresh token and clears its cookie — then points
      // the browser at the app root.
      await identityApi.signOut();
    } catch (error) {
      setPending(false);
      errorApi.post(
        new Error(
          `Échec de la déconnexion : ${
            error instanceof Error ? error.message : String(error)
          }`,
        ),
      );
      return;
    }
    if (autheliaBaseUrl) {
      // Assigned in the same task as signOut()'s own redirect to the app
      // root, so this later navigation is the one the browser follows.
      window.location.assign(autheliaLogoutUrl(autheliaBaseUrl, appBaseUrl));
    }
  };

  return { signOut, pending };
}
